import * as i0 from '@angular/core';
import { Injectable, InjectionToken, Optional, Inject, Pipe, forwardRef, NgModule } from '@angular/core';
import { of, forkJoin, Subject, share, ReplaySubject } from 'rxjs';
import { map, takeUntil, switchMap, catchError } from 'rxjs/operators';
import * as i4 from '@skyux/assets';
import { SkyAppAssetsService } from '@skyux/assets';
import * as i1 from '@angular/common/http';

const DEFAULT_LOCALE = 'en-US';
const DEFAULT_CURRENCY_CODE = 'USD';
const DEFAULT_GROUP_CHARACTER = ',';
const DEFAULT_DECIMAL_CHARACTER = '.';
/**
 * Used to format a currency within a given locale.
 */
class SkyI18nCurrencyFormatService {
    /**
     * Gets a currency's format.
     * @param isoCurrencyCode the ISO 4217 Currency Code. Defaults to 'USD'.
     * @param locale the locale. Defaults to 'en-US'. Examples: 'en-US', 'en-GB', 'fr-FR'.
     */
    getCurrencyFormat(isoCurrencyCode = DEFAULT_CURRENCY_CODE, locale = DEFAULT_LOCALE) {
        const formatter = new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: isoCurrencyCode,
        });
        const resolvedOptions = formatter.resolvedOptions();
        // We know this will be there as we give the currency options to the number format call above
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const currencyCode = resolvedOptions.currency;
        const parts = this.#formatToParts(formatter);
        /* istanbul ignore next */
        const precision = resolvedOptions.maximumFractionDigits ?? 0;
        return {
            decimalCharacter: parts.decimalCharacter,
            groupCharacter: parts.groupCharacter,
            isoCurrencyCode: currencyCode,
            locale,
            precision,
            symbol: parts.symbol,
            symbolLocation: parts.symbolLocation,
        };
    }
    #formatToParts(formatter) {
        const BIG_VALUE_TO_GET_PART_INFO = 100_000_000;
        const parts = formatter.formatToParts(BIG_VALUE_TO_GET_PART_INFO);
        const findOrDefault = (intlType, defaultValue) => parts.find((p) => p.type === intlType)?.value ?? defaultValue;
        return {
            symbol: findOrDefault('currency', ''),
            symbolLocation: parts.findIndex((p) => p.type === 'currency') === 0
                ? 'prefix'
                : 'suffix',
            decimalCharacter: findOrDefault('decimal', DEFAULT_DECIMAL_CHARACTER),
            groupCharacter: findOrDefault('group', DEFAULT_GROUP_CHARACTER),
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyI18nCurrencyFormatService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyI18nCurrencyFormatService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyI18nCurrencyFormatService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

/**
 * @internal
 */
function getLibStringForLocale(resources, preferredLocale, name) {
    const defaultLocale = 'en-US';
    function getResourcesForLocale(locale) {
        const parsedLocale = locale.toLocaleUpperCase().replace('_', '-');
        return resources[parsedLocale];
    }
    let values = getResourcesForLocale(preferredLocale);
    if (values && values[name]) {
        return values[name].message;
    }
    // Attempt to locate default resources.
    values = getResourcesForLocale(defaultLocale);
    if (values && values[name]) {
        return values[name].message;
    }
    return undefined;
}

/**
 * @internal
 * @deprecated Use `getLibStringForLocale` instead.
 */
function getStringForLocale(resources, preferredLocale, name) {
    const defaultLocale = 'en-US';
    function getResourcesForLocale(locale) {
        const parsedLocale = locale.toUpperCase().replace('_', '-');
        return resources[parsedLocale];
    }
    let values = getResourcesForLocale(preferredLocale);
    if (values && values[name]) {
        return values[name];
    }
    // Attempt to locate default resources.
    values = getResourcesForLocale(defaultLocale);
    if (values && values[name]) {
        return values[name];
    }
    return undefined;
}

// Using this class instead of the one found in `@skyux/core`
// to avoid a circular dependency.
class Format {
    static formatText(format, ...args) {
        return String(format).replace(/\{(\d+)\}/g, function (match, capture) {
            const argsIndex = parseInt(capture, 10);
            return args[argsIndex] === undefined ? match : args[argsIndex];
        });
    }
}

// #region imports
// #endregion
/**
 * @deprecated `SKY_LIB_RESOURCES_PROVIDERS` is no longer needed and will be removed in a future major version of SKY UX.
 * @internal
 */
const SKY_LIB_RESOURCES_PROVIDERS = new InjectionToken('SKY_LIB_RESOURCES_PROVIDERS');

const SKY_APP_LOCALE_PROVIDER_DEFAULT_LOCALE = 'en-US';
class SkyAppLocaleProvider {
    get defaultLocale() {
        return SKY_APP_LOCALE_PROVIDER_DEFAULT_LOCALE;
    }
    getLocaleInfo() {
        return of({
            locale: this.defaultLocale,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyAppLocaleProvider, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyAppLocaleProvider, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyAppLocaleProvider, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class SkyAppResourceNameProvider {
    getResourceName(name) {
        return of(name);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyAppResourceNameProvider, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyAppResourceNameProvider }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyAppResourceNameProvider, decorators: [{
            type: Injectable
        }] });

// #region imports
class SkyLibResourcesService {
    static { this.resources = {}; }
    #localeProvider;
    #providers;
    #resourceNameProvider;
    constructor(localeProvider, providers, resourceNameProvider) {
        this.#localeProvider = localeProvider;
        this.#providers = providers;
        this.#resourceNameProvider = resourceNameProvider;
    }
    /**
     * Adds locale resources to be used by library components.
     */
    static addResources(localeResources) {
        for (const [locale, resources] of Object.entries(localeResources)) {
            SkyLibResourcesService.resources[locale] ||= {};
            SkyLibResourcesService.resources[locale] = {
                ...SkyLibResourcesService.resources[locale],
                ...resources,
            };
        }
    }
    /**
     * Gets a resource string based on its name.
     * @param name The name of the resource string.
     * @param args Any templated args.
     */
    getString(name, ...args) {
        const mappedNameObs = this.#resourceNameProvider
            ? this.#resourceNameProvider.getResourceName(name)
            : of(name);
        const localeInfoObs = this.#localeProvider.getLocaleInfo();
        return forkJoin([mappedNameObs, localeInfoObs]).pipe(map(([mappedName, localeInfo]) => this.getStringForLocale(localeInfo, mappedName, ...args)));
    }
    /**
     * Gets a Resource String Dictionary.
     *
     * This is similar to forkJoin's dictionary syntax.
     *
     * @param dictionary a Record of **SomeObjectKey** to a Value that is either
     *   - (1) **ResourceKey**
     *   - (2) or an **Array** where the first item is the **ResourceKey** and the other items are template args.
     * @return an `Observable` of a resource string dictionary in the same shape as the passed dictionary.
     *
     * @example
     * ```typescript
     * service.getStrings({
     *    simpleKey: 'hello',
     *    arraySyntax: ['hi'],
     *    arraySyntaxWithTemplateArgs: ['template', 'a', 'b'],
     * }
     * ```
     */
    getStrings(dictionary) {
        const resources$ = {};
        for (const objKey of Object.keys(dictionary)) {
            const resource = dictionary[objKey];
            if (typeof resource === 'string') {
                resources$[objKey] = this.getString(resource);
            }
            else {
                const [key, ...templateItems] = resource;
                resources$[objKey] = this.getString(key, ...templateItems);
            }
        }
        return forkJoin(resources$);
    }
    getStringForLocale(info, name, ...args) {
        let value;
        // First, look in the static 'resources' property.
        value = getLibStringForLocale(SkyLibResourcesService.resources, info.locale, name);
        // If it's not found there, look in the providers.
        if (value === undefined && this.#providers) {
            for (const provider of this.#providers) {
                const s = provider.getString(info, name);
                if (s !== undefined) {
                    value = s;
                    break;
                }
            }
        }
        if (value !== undefined) {
            return Format.formatText(value, ...args);
        }
        return name;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyLibResourcesService, deps: [{ token: SkyAppLocaleProvider }, { token: SKY_LIB_RESOURCES_PROVIDERS, optional: true }, { token: SkyAppResourceNameProvider, optional: true }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyLibResourcesService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyLibResourcesService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: SkyAppLocaleProvider }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [SKY_LIB_RESOURCES_PROVIDERS]
                }] }, { type: SkyAppResourceNameProvider, decorators: [{
                    type: Optional
                }] }] });

// #region imports
// #endregion
class SkyLibResourcesPipe {
    #ngUnsubscribe = new Subject();
    #resourceCache = {};
    #changeDetector;
    #resourcesService;
    constructor(changeDetector, resourcesService) {
        this.#changeDetector = changeDetector;
        this.#resourcesService = resourcesService;
    }
    transform(name, ...args) {
        const cacheKey = name + JSON.stringify(args);
        if (!(cacheKey in this.#resourceCache)) {
            this.#resourcesService
                .getString(name, ...args)
                .pipe(takeUntil(this.#ngUnsubscribe))
                .subscribe((value) => {
                this.#resourceCache[cacheKey] = value;
                this.#changeDetector.markForCheck();
            });
        }
        return this.#resourceCache[cacheKey];
    }
    ngOnDestroy() {
        this.#ngUnsubscribe.next();
        this.#ngUnsubscribe.complete();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyLibResourcesPipe, deps: [{ token: i0.ChangeDetectorRef }, { token: SkyLibResourcesService }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.2.7", ngImport: i0, type: SkyLibResourcesPipe, isStandalone: false, name: "skyLibResources", pure: false }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyLibResourcesPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'skyLibResources',
                    pure: false,
                    standalone: false,
                }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }, { type: SkyLibResourcesService }] });

const defaultResources = {};
function getDefaultObs() {
    return of(defaultResources);
}
/**
 * An Angular service for interacting with resource strings.
 */
class SkyAppResourcesService {
    #resourcesObsCache = {};
    #httpObsCache = {};
    #http;
    #assets;
    #localeProvider;
    #resourceNameProvider;
    constructor(http, assets, localeProvider, resourceNameProvider) {
        this.#http = http;
        this.#assets = assets;
        // Locale provider is provided at `root`. The `Optional` here is for unit test compatibility.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this.#localeProvider = localeProvider;
        this.#resourceNameProvider = resourceNameProvider;
    }
    /**
     * Gets a resource string based on its name.
     * @param name The name of the resource string.
     * @param args Any templated args.
     */
    getString(name, ...args) {
        const localeObs = this.#localeProvider.getLocaleInfo();
        return this.#getStringForLocaleInfoObservable(localeObs, name, ...args);
    }
    /**
     * Gets a Resource String Dictionary.
     *
     * This is similar to forkJoin's dictionary syntax.
     *
     * @param dictionary a Record of **SomeObjectKey** to a Value that is either
     *   - (1) **ResourceKey**
     *   - (2) or an **Array** where the first item is the **ResourceKey** and the other items are template args.
     * @return an `Observable` of a resource string dictionary in the same shape as the passed dictionary.
     *
     * @example
     * ```typescript
     * service.getStrings({
     *    simpleKey: 'hello',
     *    arraySyntax: ['hi'],
     *    arraySyntaxWithTemplateArgs: ['template', 'a', 'b'],
     * }
     * ```
     */
    getStrings(dictionary) {
        const resources$ = {};
        for (const objKey of Object.keys(dictionary)) {
            const resource = dictionary[objKey];
            if (typeof resource === 'string') {
                resources$[objKey] = this.getString(resource);
            }
            else {
                const [key, ...templateItems] = resource;
                resources$[objKey] = this.getString(key, ...templateItems);
            }
        }
        return forkJoin(resources$);
    }
    /**
     * Gets a resource string for a specific locale based on its name.
     * @param localeInfo The locale to use.
     * @param name The name of the resource string.
     * @param args Any templated args.
     */
    getStringForLocale(localeInfo, name, ...args) {
        return this.#getStringForLocaleInfoObservable(of(localeInfo), name, ...args);
    }
    #getStringForLocaleInfoObservable(localeInfoObs, name, ...args) {
        const resourcesObs = localeInfoObs.pipe(switchMap((localeInfo) => {
            let obs;
            // Use default locale if one not provided
            const locale = localeInfo.locale || this.#localeProvider.defaultLocale;
            if (this.#resourcesObsCache[locale]) {
                return this.#resourcesObsCache[locale];
            }
            const resourcesUrl = this.#getUrlForLocale(locale) ||
                // Try falling back to the non-region-specific language.
                this.#getUrlForLocale(locale.substring(0, 2)) ||
                // Finally fall back to the default locale.
                this.#getUrlForLocale(this.#localeProvider.defaultLocale);
            if (resourcesUrl) {
                if (!this.#httpObsCache[resourcesUrl]) {
                    this.#httpObsCache[resourcesUrl] = this.#http
                        .get(resourcesUrl)
                        .pipe(share({
                        connector: () => new ReplaySubject(1),
                        resetOnError: false,
                        resetOnComplete: false,
                        resetOnRefCountZero: false,
                    }), catchError(() => {
                        // The resource file for the specified locale failed to load;
                        // fall back to the default locale if it differs from the specified
                        // locale.
                        const defaultResourcesUrl = this.#getUrlForLocale(this.#localeProvider.defaultLocale);
                        if (defaultResourcesUrl &&
                            defaultResourcesUrl !== resourcesUrl) {
                            return this.#http.get(defaultResourcesUrl);
                        }
                        return getDefaultObs();
                    }));
                }
                obs = this.#httpObsCache[resourcesUrl];
            }
            else {
                obs = getDefaultObs();
            }
            this.#resourcesObsCache[locale] = obs;
            return obs;
        }), 
        // Don't keep trying after a failed attempt to load resources, or else
        // impure pipes like resources pipe that call this service will keep
        // firing requests indefinitely every few milliseconds.
        catchError(() => getDefaultObs()));
        const mappedNameObs = this.#resourceNameProvider
            ? this.#resourceNameProvider.getResourceName(name)
            : of(name);
        return forkJoin([mappedNameObs, resourcesObs]).pipe(map(([mappedName, resources]) => {
            let resource = undefined;
            if (mappedName in resources) {
                resource = resources[mappedName];
            }
            else if (name in resources) {
                resource = resources[name];
            }
            if (resource) {
                return Format.formatText(resource.message, ...args);
            }
            return name;
        }));
    }
    #getUrlForLocale(locale) {
        return this.#assets?.getUrl(`locales/resources_${locale.replace('-', '_')}.json`);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyAppResourcesService, deps: [{ token: i1.HttpClient }, { token: forwardRef(() => SkyAppAssetsService), optional: true }, { token: SkyAppLocaleProvider, optional: true }, { token: SkyAppResourceNameProvider, optional: true }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyAppResourcesService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyAppResourcesService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i1.HttpClient }, { type: i4.SkyAppAssetsService, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [forwardRef(() => SkyAppAssetsService)]
                }] }, { type: SkyAppLocaleProvider, decorators: [{
                    type: Optional
                }] }, { type: SkyAppResourceNameProvider, decorators: [{
                    type: Optional
                }] }] });

/**
 * An Angular pipe for displaying a resource string.
 */
class SkyAppResourcesPipe {
    #ngUnsubscribe = new Subject();
    #resourceCache = {};
    #changeDetector;
    #resourcesSvc;
    constructor(changeDetector, resourcesSvc) {
        this.#changeDetector = changeDetector;
        this.#resourcesSvc = resourcesSvc;
    }
    /**
     * Transforms a named resource string into its value.
     * @param name The name of the resource string.
     */
    transform(name, ...args) {
        const cacheKey = name + JSON.stringify(args);
        if (!(cacheKey in this.#resourceCache)) {
            this.#resourcesSvc
                .getString(name, ...args)
                .pipe(takeUntil(this.#ngUnsubscribe))
                .subscribe((result) => {
                this.#resourceCache[cacheKey] = result;
                this.#changeDetector.markForCheck();
            });
        }
        return this.#resourceCache[cacheKey];
    }
    ngOnDestroy() {
        this.#ngUnsubscribe.next();
        this.#ngUnsubscribe.complete();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyAppResourcesPipe, deps: [{ token: i0.ChangeDetectorRef }, { token: SkyAppResourcesService }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.2.7", ngImport: i0, type: SkyAppResourcesPipe, isStandalone: false, name: "skyAppResources", pure: false }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyAppResourcesPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'skyAppResources',
                    pure: false,
                    standalone: false,
                }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }, { type: SkyAppResourcesService }] });

class SkyI18nModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyI18nModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.7", ngImport: i0, type: SkyI18nModule, declarations: [SkyAppResourcesPipe, SkyLibResourcesPipe], exports: [SkyAppResourcesPipe, SkyLibResourcesPipe] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyI18nModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: SkyI18nModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SkyAppResourcesPipe, SkyLibResourcesPipe],
                    exports: [SkyAppResourcesPipe, SkyLibResourcesPipe],
                }]
        }] });

// The contents of this file are mostly ported from the Angular 4.x intl.ts in order to maintain the
// old behavior of using the `Intl` API for formatting dates rather than having to register every
// supported locale.
// https://github.com/angular/angular/blob/4.4.x/packages/common/src/pipes/intl.ts
/* spell-checker:disable */
const DATE_FORMATS_SPLIT = /((?:[^yMLdHhmsazZEwGjJ']+)|(?:'(?:[^']|'')*')|(?:E+|y+|M+|L+|d+|H+|h+|J+|j+|m+|s+|a|z|Z|G+|w+))(.*)/;
const PATTERN_ALIASES = {
    // Keys are quoted so they do not get renamed during closure compilation.
    yMMMdjms: datePartGetterFactory(combine([
        digitCondition('year', 1),
        nameCondition('month', 3),
        digitCondition('day', 1),
        digitCondition('hour', 1),
        digitCondition('minute', 1),
        digitCondition('second', 1),
    ])),
    yMdjm: datePartGetterFactory(combine([
        digitCondition('year', 1),
        digitCondition('month', 1),
        digitCondition('day', 1),
        digitCondition('hour', 1),
        digitCondition('minute', 1),
    ])),
    yMMMMEEEEd: datePartGetterFactory(combine([
        digitCondition('year', 1),
        nameCondition('month', 4),
        nameCondition('weekday', 4),
        digitCondition('day', 1),
    ])),
    yMMMMd: datePartGetterFactory(combine([
        digitCondition('year', 1),
        nameCondition('month', 4),
        digitCondition('day', 1),
    ])),
    yMMMd: datePartGetterFactory(combine([
        digitCondition('year', 1),
        nameCondition('month', 3),
        digitCondition('day', 1),
    ])),
    yMd: datePartGetterFactory(combine([
        digitCondition('year', 1),
        digitCondition('month', 1),
        digitCondition('day', 1),
    ])),
    jms: datePartGetterFactory(combine([
        digitCondition('hour', 1),
        digitCondition('second', 1),
        digitCondition('minute', 1),
    ])),
    jm: datePartGetterFactory(combine([digitCondition('hour', 1), digitCondition('minute', 1)])),
};
/* spell-checker:enable */
const DATE_FORMATS = {
    // Keys are quoted so they do not get renamed.
    yyyy: datePartGetterFactory(digitCondition('year', 4)),
    yy: datePartGetterFactory(digitCondition('year', 2)),
    y: datePartGetterFactory(digitCondition('year', 1)),
    MMMM: datePartGetterFactory(nameCondition('month', 4)),
    MMM: datePartGetterFactory(nameCondition('month', 3)),
    MM: datePartGetterFactory(digitCondition('month', 2)),
    M: datePartGetterFactory(digitCondition('month', 1)),
    LLLL: datePartGetterFactory(nameCondition('month', 4)),
    L: datePartGetterFactory(nameCondition('month', 1)),
    dd: datePartGetterFactory(digitCondition('day', 2)),
    d: datePartGetterFactory(digitCondition('day', 1)),
    HH: digitModifier(hourExtractor(datePartGetterFactory(hour12Modify(digitCondition('hour', 2), false)))),
    H: hourExtractor(datePartGetterFactory(hour12Modify(digitCondition('hour', 1), false))),
    hh: digitModifier(hourExtractor(datePartGetterFactory(hour12Modify(digitCondition('hour', 2), true)))),
    h: hourExtractor(datePartGetterFactory(hour12Modify(digitCondition('hour', 1), true))),
    jj: datePartGetterFactory(digitCondition('hour', 2)),
    j: datePartGetterFactory(digitCondition('hour', 1)),
    mm: digitModifier(datePartGetterFactory(digitCondition('minute', 2))),
    m: datePartGetterFactory(digitCondition('minute', 1)),
    ss: digitModifier(datePartGetterFactory(digitCondition('second', 2))),
    s: datePartGetterFactory(digitCondition('second', 1)),
    // while ISO 8601 requires fractions to be prefixed with `.` or `,`
    // we can be just safely rely on using `sss` since we currently don't support single or two digit
    // fractions
    sss: datePartGetterFactory(digitCondition('second', 3)),
    EEEE: datePartGetterFactory(nameCondition('weekday', 4)),
    EEE: datePartGetterFactory(nameCondition('weekday', 3)),
    EE: datePartGetterFactory(nameCondition('weekday', 2)),
    E: datePartGetterFactory(nameCondition('weekday', 1)),
    a: hourClockExtractor(datePartGetterFactory(hour12Modify(digitCondition('hour', 1), true))),
    Z: timeZoneGetter('short'),
    z: timeZoneGetter('long'),
    ww: datePartGetterFactory({}), // Week of year, padded (00-53). Week 01 is the week with the
    // first Thursday of the year. not support ?
    w: datePartGetterFactory({}), // Week of year (0-53). Week 1 is the week with the first Thursday
    // of the year not support ?
    G: datePartGetterFactory(nameCondition('era', 1)),
    GG: datePartGetterFactory(nameCondition('era', 2)),
    GGG: datePartGetterFactory(nameCondition('era', 3)),
    GGGG: datePartGetterFactory(nameCondition('era', 4)),
};
function digitModifier(inner) {
    return function (date, locale) {
        const result = inner(date, locale);
        /* istanbul ignore next */
        return result.length === 1 ? '0' + result : result;
    };
}
function hourClockExtractor(inner) {
    return function (date, locale) {
        return inner(date, locale).split(' ')[1];
    };
}
function hourExtractor(inner) {
    return function (date, locale) {
        return inner(date, locale).split(' ')[0];
    };
}
function intlDateFormat(date, locale, options) {
    return new Intl.DateTimeFormat(locale, options)
        .format(date)
        .replace(/[\u200e\u200f]/g, '');
}
function timeZoneGetter(timezone) {
    // To workaround `Intl` API restriction for single timezone let format with 24 hours
    const options = {
        hour: '2-digit',
        hour12: false,
        timeZoneName: timezone,
    };
    return function (date, locale) {
        const result = intlDateFormat(date, locale, options);
        // Then extract first 3 letters that related to hours
        /* istanbul ignore else */
        if (result) {
            return result.substring(3);
        }
        /* istanbul ignore next */
        return '';
    };
}
function hour12Modify(options, value) {
    options.hour12 = value;
    return options;
}
function digitCondition(prop, len) {
    const result = {};
    result[prop] = len === 2 ? '2-digit' : 'numeric';
    return result;
}
function nameCondition(prop, len) {
    const result = {};
    if (len < 4) {
        result[prop] = len > 1 ? 'short' : 'narrow';
    }
    else {
        result[prop] = 'long';
    }
    return result;
}
function combine(options) {
    return options.reduce((merged, opt) => ({ ...merged, ...opt }), {});
}
function datePartGetterFactory(ret) {
    return (date, locale) => intlDateFormat(date, locale, ret);
}
const DATE_FORMATTER_CACHE = new Map();
function partToTime(part) {
    return part === "''" ? "'" : part.replace(/(^'|'$)/g, '').replace(/''/g, "'");
}
function dateFormatter(format, date, locale) {
    const fn = PATTERN_ALIASES[format];
    if (fn) {
        return fn(date, locale);
    }
    const cacheKey = format;
    let parts = DATE_FORMATTER_CACHE.get(cacheKey);
    /* istanbul ignore else */
    if (!parts) {
        parts = [];
        let match;
        DATE_FORMATS_SPLIT.exec(format);
        let _format = format;
        while (_format) {
            match = DATE_FORMATS_SPLIT.exec(_format);
            /*istanbul ignore else */
            if (match) {
                parts = parts.concat(match.slice(1));
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                _format = parts.pop();
            }
            else {
                parts.push(_format);
                _format = null;
            }
        }
        DATE_FORMATTER_CACHE.set(cacheKey, parts);
    }
    return parts.reduce((text, part) => {
        const dateFormatFn = DATE_FORMATS[part];
        return (text + (dateFormatFn ? dateFormatFn(date, locale) : partToTime(part)));
    }, '');
}

class SkyIntlDateFormatter {
    static format(date, locale, pattern) {
        return dateFormatter(pattern, date, locale);
    }
}

var SkyIntlNumberFormatStyle;
(function (SkyIntlNumberFormatStyle) {
    SkyIntlNumberFormatStyle[SkyIntlNumberFormatStyle["Decimal"] = 0] = "Decimal";
    SkyIntlNumberFormatStyle[SkyIntlNumberFormatStyle["Percent"] = 1] = "Percent";
    SkyIntlNumberFormatStyle[SkyIntlNumberFormatStyle["Currency"] = 2] = "Currency";
})(SkyIntlNumberFormatStyle || (SkyIntlNumberFormatStyle = {}));

class SkyIntlNumberFormatter {
    static format(num, locale, style, opts = {}) {
        const { minimumIntegerDigits, minimumFractionDigits, maximumFractionDigits, currency, currencySign = 'standard', currencyDisplay = 'code', } = opts;
        const options = {
            minimumIntegerDigits,
            minimumFractionDigits,
            maximumFractionDigits,
            style: SkyIntlNumberFormatStyle[style].toLowerCase(),
            currencySign,
        };
        if (style === SkyIntlNumberFormatStyle.Currency) {
            options.currency = typeof currency === 'string' ? currency : undefined;
            options.currencyDisplay = currencyDisplay;
        }
        return new Intl.NumberFormat(locale, options).format(num);
    }
}

// #endregion
/**
 * @deprecated `SkyLibResourcesProvider` is no longer needed and will be removed in a future major version of SKY UX.
 * @internal
 */
class SkyLibResourcesProvider {
}

/**
 * Generated bundle index. Do not edit.
 */

export { SKY_LIB_RESOURCES_PROVIDERS, SkyAppLocaleProvider, SkyAppResourceNameProvider, SkyAppResourcesPipe, SkyAppResourcesService, SkyI18nCurrencyFormatService, SkyI18nModule, SkyIntlDateFormatter, SkyIntlNumberFormatStyle, SkyIntlNumberFormatter, SkyLibResourcesPipe, SkyLibResourcesProvider, SkyLibResourcesService, getLibStringForLocale, getStringForLocale };
